/**
 * What happened in a round, and how much of it was captured.
 *
 * Two pure readings of a finished round, shared so the server, the replay page
 * and the scripts cannot disagree about either:
 *
 *   - `RoundSummary` — the kitchen's outcome counted off the event tape: orders
 *     spawned, dishes served, matched, unmatched, expired, the score broken down
 *     by where it came from, and per-player activity. It is computed from engine
 *     events and nothing else, so the log alone reproduces it.
 *   - `SessionDataQuality` — whether the data a researcher needs from this round
 *     actually exists: one verdict per seat's recording with the reasons, and a
 *     list of round-level flags. A round is never refused or dropped for being
 *     incomplete; it is stored and *labelled*, and this is the label.
 *
 * Both are additive. Every field that carries them is optional, and a reader
 * that finds neither is reading a round recorded before they existed.
 *
 * Only *types* are imported from `./index`, so there is no runtime cycle — the
 * same rule `fold.ts` keeps. The one number this needs from there, the tick
 * period, is passed in by the caller.
 */

import type {
  AudioArtifact,
  EventFrame,
  GameEvent,
  PlayerId,
  SeatKind,
  SessionIndexEntry,
  StoredConsent,
  StudyCondition,
} from './index'

// ---------------------------------------------------------------------------
// Round summary
// ---------------------------------------------------------------------------

/** One seat's share of the round. Counts only — never a judgement. */
export interface PlayerRoundSummary {
  served: number
  matched: number
  unmatched: number
  /** The score this seat's serves earned, matched or not. */
  scoreFromServes: number
  pickedUp: number
  putDown: number
  chopped: number
  washed: number
  plated: number
  trashed: number
  pansFilled: number
  firesExtinguished: number
  actionsFailed: number
  /** Raw key presses the server accepted (`INPUT` events). */
  inputs: number
  moves: number
  utterances: number
  plans: number
  /** Mid-round drops and returns, as the tape recorded them. */
  disconnects: number
  reconnects: number
  /** WebRTC round-trip samples recorded for this seat. */
  rtt: { samples: number; meanMs: number | null; maxMs: number | null }
}

export interface RoundSummary {
  /** Bumped only if a field changes meaning. Additions do not bump it. */
  version: 1
  ordersSpawned: number
  /** Every `SERVED`, matched to a ticket or not. */
  dishesServed: number
  dishesMatched: number
  /** A plate handed in that matched no open ticket. */
  dishesUnmatched: number
  ordersExpired: number
  /** Tickets still open at the whistle: spawned, never filled, never expired. */
  ordersOpen: number
  /** Matched serves by the dish the ticket asked for. */
  servedByDish: Record<string, number>
  /** Expired tickets by the dish they asked for. */
  expiredByDish: Record<string, number>
  score: {
    /** `ROUND_ENDED.score` when the tape has one, else the sum of the deltas. */
    final: number
    fromServes: number
    fromExpiries: number
    /** Anything the two above do not explain. 0 in every well-formed round. */
    other: number
  }
  firesStarted: number
  collisions: number
  contested: number
  perPlayer: Record<PlayerId, PlayerRoundSummary>
  /** Every event type on the tape and how many times it occurred. */
  events: Record<string, number>
  /** The last tick the tape reached. */
  lastTick: number
}

function emptyPlayer(): PlayerRoundSummary {
  return {
    served: 0,
    matched: 0,
    unmatched: 0,
    scoreFromServes: 0,
    pickedUp: 0,
    putDown: 0,
    chopped: 0,
    washed: 0,
    plated: 0,
    trashed: 0,
    pansFilled: 0,
    firesExtinguished: 0,
    actionsFailed: 0,
    inputs: 0,
    moves: 0,
    utterances: 0,
    plans: 0,
    disconnects: 0,
    reconnects: 0,
    rtt: { samples: 0, meanMs: null, maxMs: null },
  }
}

export interface RoundTally {
  /** Count one frame's events. Frames may arrive in any batch size. */
  add(frame: Pick<EventFrame, 'tick' | 'events'>): void
  /** The summary as of the last frame added. Cheap; safe to call repeatedly. */
  summary(): RoundSummary
}

/**
 * An incremental counter over the tape.
 *
 * Incremental because the server needs the answer at the whistle, in the same
 * call that emits `ROUND_ENDED`, and re-reading a log there would mean a read of
 * storage on the hot path. `summariseRound` below is the same counter run over
 * a stored log, so the two can be compared.
 */
export function createRoundTally(): RoundTally {
  const events: Record<string, number> = {}
  const orders = new Map<string, string>()
  const perPlayer: Record<PlayerId, PlayerRoundSummary> = {}
  const rttSum: Record<PlayerId, number> = {}
  let ordersSpawned = 0
  let dishesServed = 0
  let dishesMatched = 0
  let ordersExpired = 0
  const servedByDish: Record<string, number> = {}
  const expiredByDish: Record<string, number> = {}
  let fromServes = 0
  let fromExpiries = 0
  let finalScore: number | null = null
  let firesStarted = 0
  let collisions = 0
  let contested = 0
  let lastTick = 0

  const seat = (id: PlayerId | 'system' | undefined): PlayerRoundSummary | null => {
    if (!id || id === 'system') return null
    let row = perPlayer[id]
    if (!row) {
      row = emptyPlayer()
      perPlayer[id] = row
    }
    return row
  }

  const one = (event: GameEvent): void => {
    events[event.type] = (events[event.type] ?? 0) + 1
    if (event.tick > lastTick) lastTick = event.tick
    switch (event.type) {
      case 'ROUND_STARTED':
        for (const player of event.players) seat(player.id)
        break
      case 'ORDER_SPAWNED':
        ordersSpawned += 1
        orders.set(event.order.id, event.order.dishId)
        break
      case 'ORDER_EXPIRED': {
        ordersExpired += 1
        fromExpiries += event.scoreDelta
        const dish = orders.get(event.orderId) ?? 'unknown'
        expiredByDish[dish] = (expiredByDish[dish] ?? 0) + 1
        break
      }
      case 'SERVED': {
        dishesServed += 1
        fromServes += event.scoreDelta
        const row = seat(event.playerId)
        if (row) {
          row.served += 1
          row.scoreFromServes += event.scoreDelta
        }
        if (event.matchedOrderId !== null) {
          dishesMatched += 1
          if (row) row.matched += 1
          const dish = orders.get(event.matchedOrderId) ?? 'unknown'
          servedByDish[dish] = (servedByDish[dish] ?? 0) + 1
        } else if (row) {
          row.unmatched += 1
        }
        break
      }
      case 'ROUND_ENDED':
        finalScore = event.score
        break
      case 'PICKED_UP':
        bump(seat(event.playerId), 'pickedUp')
        break
      case 'PUT_DOWN':
        bump(seat(event.playerId), 'putDown')
        break
      case 'CHOPPED':
        bump(seat(event.playerId), 'chopped')
        break
      case 'WASHED':
        bump(seat(event.playerId), 'washed')
        break
      case 'PLATED':
        bump(seat(event.playerId), 'plated')
        break
      case 'TRASHED':
        bump(seat(event.playerId), 'trashed')
        break
      case 'PAN_FILLED':
        bump(seat(event.playerId), 'pansFilled')
        break
      case 'FIRE_STARTED':
        firesStarted += 1
        break
      case 'FIRE_EXTINGUISHED':
        bump(seat(event.playerId), 'firesExtinguished')
        break
      case 'ACTION_FAILED':
        bump(seat(event.playerId), 'actionsFailed')
        break
      case 'INPUT':
        bump(seat(event.playerId), 'inputs')
        break
      case 'PLAYER_MOVED':
        bump(seat(event.playerId), 'moves')
        break
      case 'UTTERANCE':
        bump(seat(event.playerId), 'utterances')
        break
      case 'PLAN':
        bump(seat(event.playerId), 'plans')
        break
      case 'PLAYER_DISCONNECTED':
        bump(seat(event.playerId), 'disconnects')
        break
      case 'PLAYER_CONNECTED':
        bump(seat(event.playerId), 'reconnects')
        break
      case 'COLLISION':
        collisions += 1
        break
      case 'CONTESTED':
        contested += 1
        break
      case 'NETWORK_SAMPLE': {
        const row = seat(event.playerId)
        if (!row) break
        row.rtt.samples += 1
        rttSum[event.playerId] = (rttSum[event.playerId] ?? 0) + event.rttMs
        row.rtt.meanMs = Math.round(rttSum[event.playerId] / row.rtt.samples)
        row.rtt.maxMs = Math.max(row.rtt.maxMs ?? 0, event.rttMs)
        break
      }
      default:
        break
    }
  }

  return {
    add(frame) {
      if (frame.tick > lastTick) lastTick = frame.tick
      for (const event of frame.events) one(event)
    },
    summary() {
      const summed = fromServes + fromExpiries
      const final = finalScore ?? summed
      return {
        version: 1,
        ordersSpawned,
        dishesServed,
        dishesMatched,
        dishesUnmatched: dishesServed - dishesMatched,
        ordersExpired,
        ordersOpen: Math.max(0, ordersSpawned - dishesMatched - ordersExpired),
        servedByDish: { ...servedByDish },
        expiredByDish: { ...expiredByDish },
        score: { final, fromServes, fromExpiries, other: final - summed },
        firesStarted,
        collisions,
        contested,
        perPlayer: Object.fromEntries(
          Object.entries(perPlayer).map(([id, row]) => [id, { ...row, rtt: { ...row.rtt } }]),
        ),
        events: { ...events },
        lastTick,
      }
    },
  }
}

type CountKey = {
  [K in keyof PlayerRoundSummary]: PlayerRoundSummary[K] extends number ? K : never
}[keyof PlayerRoundSummary]

function bump(row: PlayerRoundSummary | null, key: CountKey): void {
  if (row) row[key] += 1
}

/** The same counter, over a stored log's frames. */
export function summariseRound(frames: ReadonlyArray<Pick<EventFrame, 'tick' | 'events'>>): RoundSummary {
  const tally = createRoundTally()
  for (const frame of frames) tally.add(frame)
  return tally.summary()
}

// ---------------------------------------------------------------------------
// Data quality
// ---------------------------------------------------------------------------

/**
 * The flag vocabulary. Codes, not sentences, so a script can filter on them;
 * every flag also carries a sentence for the person reading the list.
 */
export type DataQualityCode =
  /** A seat that should have been recorded has no audio at all. */
  | 'AUDIO_MISSING'
  /** A recording exists but has holes, or its accounting disagrees. */
  | 'AUDIO_PARTIAL'
  /** The bytes did not open as the container they claimed to be. */
  | 'AUDIO_INVALID_SIGNATURE'
  /** The bytes could not be stored (and were not spooled either). */
  | 'AUDIO_UPLOAD_FAILED'
  /** The recording covers clearly less of the round than the round lasted. */
  | 'AUDIO_LOW_COVERAGE'
  /** The recorder restarted mid-round (a reload): more than one file. */
  | 'AUDIO_SEGMENTED'
  /** The seat never said "that is all of it" — its tail may be missing. */
  | 'AUDIO_NO_FINAL_REPORT'
  /** The recording carries no sync beep; align it by arrival time only. */
  | 'AUDIO_NO_SYNC_BEEP'
  /** A seat dropped during the round. */
  | 'PLAYER_DISCONNECTED'
  /** The round did not run to the whistle. */
  | 'ROUND_ABORTED'
  /** The round was ended by a server shutdown or restart. */
  | 'SERVER_SHUTDOWN'
  /** The round was written from the crash spool after the process died. */
  | 'SERVER_CRASH_RECOVERED'
  /** The stored log could not be read back when the round was finalised. */
  | 'LOG_NOT_VERIFIED'
  /** The stored log has no header line. */
  | 'LOG_HEADER_MISSING'
  /** One or more objects were spooled locally and uploaded later. */
  | 'STORAGE_DEFERRED'
  /** A human seat carries no recording agreement (solo rooms never ask). */
  | 'CONSENT_NOT_RECORDED'
  /** A seat was played from something other than a web browser. */
  | 'NON_BROWSER_CLIENT'

export interface DataQualityFlag {
  code: DataQualityCode
  detail: string
  /** The seat the flag is about, when it is about one. */
  playerId?: PlayerId
}

/** Coarse, from the user-agent header: enough to group failures by browser. */
export interface ClientInfo {
  browser: string | null
  os: string | null
  mobile: boolean
}

export type SeatAudioVerdict = 'complete' | 'incomplete' | 'missing' | 'not_expected'

export interface SeatAudioQuality {
  playerId: PlayerId
  seat: number
  kind: SeatKind | null
  /** Whether this seat should have produced a recording at all. */
  expected: boolean
  verdict: SeatAudioVerdict
  /** The stored `AudioStatus`, verbatim, or `null` when there is no artifact. */
  status: AudioArtifact['status'] | null
  reasons: string[]
  chunkCount: number
  bytes: number
  /**
   * Seconds of audio, estimated from the chunk count and the recorder's
   * one-second timeslice. `null` when nothing arrived.
   */
  approxSeconds: number | null
  /** `approxSeconds / roundSeconds`, 0..1+. `null` when not expected. */
  coverage: number | null
  segments: number
  client: ClientInfo | null
}

export interface SessionDataQuality {
  version: 1
  /** `incomplete` whenever any flag below is a data loss rather than a note. */
  status: 'complete' | 'incomplete'
  audio: {
    /** Whether this round needs audio at all: a person in a multi-seat room. */
    expected: boolean
    status: 'complete' | 'incomplete' | 'not_expected'
    perPlayer: SeatAudioQuality[]
  }
  flags: DataQualityFlag[]
  /** Objects that missed storage at the whistle and were spooled for retry. */
  deferredKeys?: string[]
}

/** The recorder's timeslice. Chunks are therefore roughly seconds. */
export const RECORDER_TIMESLICE_MS = 1000

/**
 * Below this share of the round, a recording is "clearly short". Every whole
 * recording in the bucket covers 96–99% of its round (the recorder starts a
 * beat after the whistle); a seat that dropped 19 s before the end of a 180 s
 * round covers 87% and was labelled `ok`. So: 90%.
 */
export const MIN_AUDIO_COVERAGE = 0.9

/** Flags that describe a loss of data rather than a note about it. */
const LOSS_CODES: ReadonlySet<DataQualityCode> = new Set<DataQualityCode>([
  'AUDIO_MISSING',
  'AUDIO_PARTIAL',
  'AUDIO_INVALID_SIGNATURE',
  'AUDIO_UPLOAD_FAILED',
  'AUDIO_LOW_COVERAGE',
  'AUDIO_SEGMENTED',
  'AUDIO_NO_FINAL_REPORT',
  'LOG_NOT_VERIFIED',
  'LOG_HEADER_MISSING',
])

export interface AssessInput {
  /** `TICK_MS` from this package; passed in to keep this module type-only. */
  tickMs: number
  condition: StudyCondition | null
  reason: 'timer' | 'aborted'
  /** The round's nominal length, and the last tick the log reached. */
  durationTicks: number
  lastTick: number
  players: Array<{ id: PlayerId; seat: number; kind: SeatKind | null; consent?: StoredConsent | null }>
  audio: AudioArtifact[]
  summary?: RoundSummary | null
  logVerified?: boolean
  headerWritten?: boolean
  deferredKeys?: string[]
  clients?: Record<PlayerId, ClientInfo | null>
  /** Why the round ended, when it was not the room's own doing. */
  endedBy?: 'shutdown' | 'crash' | null
  /** Seats in the room. A one-seat room records nothing by design. */
  seatCount?: number
  /**
   * Skip the coverage estimate — for a caller judging from an index row, which
   * knows each seat's status but not how many chunks it holds.
   */
  skipCoverage?: boolean
}

/**
 * Judge one round.
 *
 * Expected audio is the rule the client already follows: a person in a room
 * with more than one seat records; an agent never does, and solo never does.
 * A seat of unknown kind in a multi-seat room is assumed to be a person, so a
 * lost recording is never excused by a missing label.
 */
export function assessSession(input: AssessInput): SessionDataQuality {
  const flags: DataQualityFlag[] = []
  const seatCount = input.seatCount ?? input.players.length
  const multiSeat = seatCount > 1 && input.condition !== 'solo'
  const roundTicks = input.reason === 'timer' ? Math.max(input.durationTicks, input.lastTick) : input.lastTick
  const roundSeconds = (roundTicks * input.tickMs) / 1000

  const perPlayer: SeatAudioQuality[] = input.players.map((player) => {
    const artifact = input.audio.find((a) => a.playerId === player.id) ?? null
    const expected = multiSeat && player.kind !== 'agent'
    // The whole recording, across every file a restarted recorder left.
    const chunkCount = artifact?.totalChunkCount ?? artifact?.chunkCount ?? 0
    const bytes = artifact?.totalBytes ?? artifact?.bytes ?? 0
    const approxSeconds = chunkCount > 0 ? (chunkCount * RECORDER_TIMESLICE_MS) / 1000 : null
    const coverage =
      expected && roundSeconds > 0 && !input.skipCoverage
        ? Math.round(((approxSeconds ?? 0) / roundSeconds) * 1000) / 1000
        : null
    const segments = artifact?.segments?.length ?? (artifact?.key ? 1 : 0)
    const reasons: string[] = []
    const seatFlag = (code: DataQualityCode, detail: string): void => {
      reasons.push(detail)
      flags.push({ code, detail, playerId: player.id })
    }

    let verdict: SeatAudioVerdict = 'not_expected'
    if (expected) {
      if (!artifact || chunkCount === 0) {
        verdict = 'missing'
        seatFlag('AUDIO_MISSING', `no audio arrived from seat ${player.seat}`)
      } else {
        verdict = 'complete'
        if (artifact.status === 'upload_failed') {
          verdict = 'incomplete'
          seatFlag('AUDIO_UPLOAD_FAILED', `seat ${player.seat}: the recording could not be stored`)
        } else if (artifact.status === 'invalid_signature') {
          verdict = 'incomplete'
          seatFlag('AUDIO_INVALID_SIGNATURE', `seat ${player.seat}: the file does not open as ${artifact.mimeType}`)
        } else if (artifact.status === 'partial') {
          verdict = 'incomplete'
          const why = artifact.flags.map((f) => f.event).join(', ') || 'integrity check failed'
          seatFlag('AUDIO_PARTIAL', `seat ${player.seat}: ${why}`)
        }
        if (segments > 1) {
          verdict = 'incomplete'
          seatFlag('AUDIO_SEGMENTED', `seat ${player.seat}: the recorder restarted ${segments - 1} time(s); ${segments} files`)
        }
        if (artifact.reported === false) {
          verdict = 'incomplete'
          seatFlag('AUDIO_NO_FINAL_REPORT', `seat ${player.seat}: no end-of-recording report; the tail may be missing`)
        }
        if (coverage !== null && coverage < MIN_AUDIO_COVERAGE) {
          verdict = 'incomplete'
          seatFlag(
            'AUDIO_LOW_COVERAGE',
            `seat ${player.seat}: ~${Math.round(approxSeconds ?? 0)}s of audio for a ${Math.round(roundSeconds)}s round (${Math.round(coverage * 100)}%)`,
          )
        }
        if (artifact.syncBeep === false) {
          seatFlag('AUDIO_NO_SYNC_BEEP', `seat ${player.seat}: recorded without the sync beep; align by arrival tick`)
        }
      }
    }

    return {
      playerId: player.id,
      seat: player.seat,
      kind: player.kind,
      expected,
      verdict,
      status: artifact?.status ?? null,
      reasons,
      chunkCount,
      bytes,
      approxSeconds,
      coverage,
      segments,
      client: input.clients?.[player.id] ?? null,
    }
  })

  // Seat-level facts that are not about the audio file itself.
  for (const player of input.players) {
    const row = input.summary?.perPlayer[player.id]
    if (row && row.disconnects > 0) {
      flags.push({
        code: 'PLAYER_DISCONNECTED',
        detail: `seat ${player.seat} dropped ${row.disconnects} time(s) and came back ${row.reconnects} time(s)`,
        playerId: player.id,
      })
    }
    if (player.kind === 'human' && player.consent === null) {
      flags.push({
        code: 'CONSENT_NOT_RECORDED',
        detail: `seat ${player.seat} carries no recording agreement${multiSeat ? '' : ' (solo rooms do not ask)'}`,
        playerId: player.id,
      })
    }
    const client = input.clients?.[player.id]
    if (player.kind === 'human' && input.clients && player.id in input.clients && (!client || !client.browser)) {
      flags.push({
        code: 'NON_BROWSER_CLIENT',
        detail: `seat ${player.seat} was not played from a recognised web browser (a script or test client?)`,
        playerId: player.id,
      })
    }
  }

  if (input.reason === 'aborted') {
    flags.push({ code: 'ROUND_ABORTED', detail: `ended at tick ${input.lastTick} of ${input.durationTicks}` })
  }
  if (input.endedBy === 'shutdown') {
    flags.push({ code: 'SERVER_SHUTDOWN', detail: 'ended by a server shutdown or deploy' })
  }
  if (input.endedBy === 'crash') {
    flags.push({ code: 'SERVER_CRASH_RECOVERED', detail: 'written from the crash spool after the server stopped' })
  }
  if (input.logVerified === false) {
    flags.push({ code: 'LOG_NOT_VERIFIED', detail: 'the stored log could not be read back at the whistle' })
  } else if (input.logVerified === true && input.headerWritten === false) {
    flags.push({ code: 'LOG_HEADER_MISSING', detail: 'the stored log has no header line' })
  }
  if (input.deferredKeys && input.deferredKeys.length > 0) {
    flags.push({
      code: 'STORAGE_DEFERRED',
      detail: `${input.deferredKeys.length} object(s) spooled locally for a later upload: ${input.deferredKeys.join(', ')}`,
    })
  }

  const expectedSeats = perPlayer.filter((p) => p.expected)
  const audioStatus: SessionDataQuality['audio']['status'] =
    expectedSeats.length === 0
      ? 'not_expected'
      : expectedSeats.every((p) => p.verdict === 'complete')
        ? 'complete'
        : 'incomplete'

  return {
    version: 1,
    status: flags.some((f) => LOSS_CODES.has(f.code)) ? 'incomplete' : 'complete',
    audio: { expected: expectedSeats.length > 0, status: audioStatus, perPlayer },
    flags,
    ...(input.deferredKeys && input.deferredKeys.length > 0 ? { deferredKeys: [...input.deferredKeys] } : {}),
  }
}

/** The flag codes of a quality block, de-duplicated, for an index row. */
export function flagCodes(quality: SessionDataQuality | null | undefined): DataQualityCode[] {
  if (!quality) return []
  return [...new Set(quality.flags.map((f) => f.code))]
}

/** Whether a round has audio a researcher would want to know is not whole. */
export function audioIncomplete(quality: SessionDataQuality | null | undefined): boolean {
  return quality?.audio.status === 'incomplete'
}

/**
 * The verdict for one index row, whether or not it carries one.
 *
 * A row written since the verdict existed is believed. An older row is judged
 * from what a row knows — its condition, its seats' kinds and each seat's
 * audio status, in seat order — which is enough to find a missing or partial
 * recording without opening a manifest. A rebuilt index replaces these
 * judgements with each manifest's own. A one-seat round an older build called
 * `human-human` is judged as the solo round it was.
 */
export function qualityOfRow(
  row: SessionIndexEntry,
  tickMs: number,
): NonNullable<SessionIndexEntry['quality']> {
  if (row.quality) return row.quality
  const condition = row.condition === 'human-human' && row.playerCount === 1 ? 'solo' : row.condition
  const quality = assessSession({
    tickMs,
    condition,
    reason: row.reason,
    durationTicks: row.lastTick,
    lastTick: row.lastTick,
    players: row.seats.map((seat, i) => ({ id: `seat-${i + 1}`, seat: i + 1, kind: seat.kind })),
    audio: row.audioStatus.map((status, i) => ({
      playerId: `seat-${i + 1}`,
      seat: i + 1,
      key: status === 'missing' ? null : `seat-${i + 1}`,
      mimeType: '',
      bytes: status === 'missing' ? 0 : 1,
      chunkCount: status === 'missing' ? 0 : 1,
      status,
      flags: [],
    })),
    seatCount: row.playerCount,
    skipCoverage: true,
  })
  return { status: quality.status, audio: quality.audio.status, flags: flagCodes(quality) }
}

// ---------------------------------------------------------------------------
// Client identification
// ---------------------------------------------------------------------------

/**
 * Browser family and OS from a user-agent header, or `null`s.
 *
 * Deliberately coarse — family and major version only — because the question
 * is "do the failures cluster on one browser", and the full string is a
 * fingerprint nobody needs to keep. A request with no user agent at all, or
 * one from a non-browser HTTP library, comes back with `browser: null`.
 */
export function parseUserAgent(ua: string | null | undefined): ClientInfo {
  const text = typeof ua === 'string' ? ua.slice(0, 512) : ''
  const mobile = /Mobi|Android|iPhone|iPad/i.test(text)
  const os = /Windows/.test(text)
    ? 'Windows'
    : /iPhone|iPad|iPod/.test(text)
      ? 'iOS'
      : /Mac OS X|Macintosh/.test(text)
        ? 'macOS'
        : /Android/.test(text)
          ? 'Android'
          : /CrOS/.test(text)
            ? 'ChromeOS'
            : /Linux/.test(text)
              ? 'Linux'
              : null
  const pick = (pattern: RegExp, name: string): string | null => {
    const match = text.match(pattern)
    return match ? `${name} ${match[1]}` : null
  }
  const browser =
    pick(/Edg\/(\d+)/, 'Edge') ??
    pick(/OPR\/(\d+)/, 'Opera') ??
    pick(/Firefox\/(\d+)/, 'Firefox') ??
    pick(/CriOS\/(\d+)/, 'Chrome') ??
    pick(/Chrome\/(\d+)/, 'Chrome') ??
    (/Safari\//.test(text) ? pick(/Version\/(\d+)/, 'Safari') ?? 'Safari' : null)
  return { browser, os, mobile }
}
